/**
 * Shared environment variable construction for sandbox commands
 *
 * Centralizes the env vars block that was duplicated across exec, run, launch, and exec-batch.
 */

import { getProxyUrl } from '@spacecode/shared/remote';
import type { ProxyMode } from '@spacecode/shared/remote';

export interface BuildEnvVarsOptions {
  /** GitHub token for repo access */
  ghToken?: string;
  /** API proxy mode */
  proxyMode?: ProxyMode;
  /** New project name (for launch command) */
  newProjectName?: string;
  /** Git user.name from local config */
  gitUserName?: string;
  /** Git user.email from local config */
  gitUserEmail?: string;
  /** Skip proxy — omit ANTHROPIC_API_KEY and ANTHROPIC_BASE_URL (BYOK mode) */
  skipProxy?: boolean;
  /** Use a custom API key directly (no proxy, no BASE_URL) */
  customApiKey?: string;
}

/**
 * Build the environment variables record to inject into a sandbox.
 *
 * Includes ANTHROPIC_API_KEY and ANTHROPIC_BASE_URL (gateway routing), and
 * optionally GH_TOKEN, NEW_PROJECT_NAME, and git identity.
 */
export function buildEnvVars(options: BuildEnvVarsOptions): Record<string, string> {
  const envVars: Record<string, string> = {};

  if (options.customApiKey) {
    // User-provided API key — route directly to Anthropic (no proxy)
    envVars.ANTHROPIC_API_KEY = options.customApiKey;
  } else if (!options.skipProxy) {
    // Default: route through SpaceCode proxy
    envVars.ANTHROPIC_API_KEY = 'proxy';
    envVars.ANTHROPIC_BASE_URL = getProxyUrl(options.proxyMode);
  }
  // skipProxy with no customApiKey: omit both vars (BYOK)

  if (options.ghToken) envVars.GH_TOKEN = options.ghToken;
  if (options.newProjectName) envVars.NEW_PROJECT_NAME = options.newProjectName;
  if (options.gitUserName) envVars.GIT_USER_NAME = options.gitUserName;
  if (options.gitUserEmail) envVars.GIT_USER_EMAIL = options.gitUserEmail;

  return envVars;
}
